import React, { useState, useEffect } from 'react';
import { X, MagnifyingGlass, CheckCircle, Plus, ArrowRight, Books, Clock } from '@phosphor-icons/react';
import { useAuthStore } from '../stores/authStore';
import { dispatchRateLimitError } from '../utils/api';
import { RateLimitError } from '../types/tier';

interface ContentItem {
  id: string;
  title: string;
  summary?: string;
  category?: string;
  url?: string;
  reading_time_minutes?: number;
  difficulty?: string;
}

interface ManualPathCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ManualPathCreator({ isOpen, onClose, onSuccess }: ManualPathCreatorProps) {
  const { token } = useAuthStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [pathTitle, setPathTitle] = useState('');
  const [pathDescription, setPathDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadContentItems();
      // Reset state when modal opens
      setStep(1);
      setPathTitle('');
      setPathDescription('');
      setSelectedItems(new Set());
      setSearchQuery('');
    }
  }, [isOpen]);

  const loadContentItems = async () => {
    if (!token) return;
    
    try {
      setIsLoading(true);
      // Request 100 items (max allowed by backend)
      const response = await fetch('/api/content?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // The backend already filters out notes and screenshots for us
        // So we can use the data directly
        const items = data.data || [];
        console.log('📚 Loaded content items:', items.length);
        setContentItems(items);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleItemSelection = (itemId: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    setSelectedItems(newSelection);
  };

  const handleCreatePath = async () => {
    if (!token || selectedItems.size === 0 || !pathTitle.trim()) return;

    try {
      setIsCreating(true);
      const response = await fetch('/api/learning-paths/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic: pathTitle.trim(),
          contentIds: Array.from(selectedItems),
          skipSemanticFilter: true // Manual creation - user explicitly chose these items
        })
      });

      if (response.ok) {
        onSuccess();
        onClose();
      } else if (response.status === 429) {
        // Rate limit exceeded - dispatch event for upgrade modal
        const errorData = await response.json() as RateLimitError;
        dispatchRateLimitError(errorData);
        alert(`Learning path limit reached. ${errorData.usage?.resetIn ? `Resets ${errorData.usage.resetIn}.` : ''}`);
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to create trail');
      }
    } catch (error) {
      console.error('Failed to create learning path:', error);
      alert('Failed to create trail. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const filteredItems = contentItems.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title.toLowerCase().includes(query) ||
      item.summary?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query)
    );
  });

  const selectedItemsData = contentItems.filter(item => selectedItems.has(item.id));
  const totalReadingTime = selectedItemsData.reduce((sum, item) => sum + (item.reading_time_minutes || 10), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Trail
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {step === 1 ? 'Select content from your library' : 'Name your trail'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X weight="bold" className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
              step === 1 
                ? 'bg-blue-500 text-white' 
                : 'bg-emerald-500 text-white'
            }`}>
              {step === 1 ? '1' : <CheckCircle weight="fill" className="w-5 h-5" />}
            </div>
            <div className={`h-0.5 flex-1 ${step === 2 ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
              step === 2 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}>
              2
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            <>
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlass 
                    weight="bold" 
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" 
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search your library..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                             rounded-xl text-gray-900 dark:text-white placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Selected Count */}
              {selectedItems.size > 0 && (
                <div className={`mb-4 p-4 rounded-xl border ${
                  selectedItems.size < 2 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/30'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedItems.size < 2 
                          ? 'text-amber-900 dark:text-amber-100'
                          : 'text-blue-900 dark:text-blue-100'
                      }`}>
                        {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'} selected
                        {selectedItems.size < 2 && ' (minimum 2 required)'}
                      </p>
                      <p className={`text-xs mt-0.5 ${
                        selectedItems.size < 2 
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        ~{Math.ceil(totalReadingTime / 60)} hours estimated
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedItems(new Set())}
                      className={`text-sm font-medium ${
                        selectedItems.size < 2 
                          ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                          : 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300'
                      }`}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}

              {/* Content List */}
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="w-3 h-3 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
                    <span className="w-3 h-3 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                    <span className="w-3 h-3 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">Loading your content...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No content found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItems.map((item) => {
                    const isSelected = selectedItems.has(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleItemSelection(item.id)}
                        className={`w-full text-left p-4 rounded-xl border transition-all ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-5 h-5 rounded border-2 mt-0.5 flex items-center justify-center ${
                            isSelected
                              ? 'bg-blue-500 border-blue-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && <CheckCircle weight="fill" className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-1">
                              {item.title}
                            </h4>
                            {item.summary && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                                {item.summary}
                              </p>
                            )}
                            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                              {item.category && (
                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                                  {item.category}
                                </span>
                              )}
                              {item.reading_time_minutes && (
                                <span className="flex items-center gap-1">
                                  <Clock weight="duotone" className="w-3 h-3" />
                                  {item.reading_time_minutes} min
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Step 2: Path Details */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Trail Title *
                  </label>
                  <input
                    type="text"
                    value={pathTitle}
                    onChange={(e) => setPathTitle(e.target.value)}
                    placeholder="e.g., Machine Learning Fundamentals"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                             rounded-xl text-gray-900 dark:text-white placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description (optional)
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    AI will generate a description, but you can add your own notes here.
                  </p>
                  <textarea
                    value={pathDescription}
                    onChange={(e) => setPathDescription(e.target.value)}
                    placeholder="Add any specific learning goals or notes..."
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                             rounded-xl text-gray-900 dark:text-white placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Summary */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Path Summary</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Books weight="duotone" className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Content Items</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedItems.size}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock weight="duotone" className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Time</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          ~{Math.ceil(totalReadingTime / 60)} hours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={step === 1 ? onClose : () => setStep(1)}
            className="px-5 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 
                     rounded-xl font-medium transition-colors"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={selectedItems.size < 2}
              className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>Next</span>
              <ArrowRight weight="bold" className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleCreatePath}
              disabled={!pathTitle.trim() || isCreating}
              className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                      <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus weight="bold" className="w-5 h-5" />
                  <span>Create Path</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

